// @ts-nocheck
import PropTypes from "prop-types";
import {transformFiguresToAnimalData} from "../../../../../utils/lotteryDataTransformers";
import {
  animalButtonH156Cls,
  animalGridCls,
  animalIconCls,
  animalNumberCls,
  animalRowC5Cls,
  betChipAmountCls,
  betChipCls,
  betChipDeletionModeCls,
  bottomLeftCls,
  bottomRightCls,
  colorClsMap,
  gridContainerCls,
  special1Cls,
  special2Cls,
  specialButtonBaseCls,
  specialNumberCls,
  topSectionH180C2Cls,
} from "../_numberGridShared";

/**
 * NumberGrid - Renders the 57-animal betting grid
 *
 * This component displays:
 * - Special animals: Delfín (0) and Ballena (00)
 * - Main grid: 55 animals (1-55) in a 5-column layout
 *
 * The gameIdString prop allows for game-specific behavior:
 * - Different API endpoints based on the game
 * - Game-specific animal configurations from API data
 * - Custom styling or layout adjustments per game
 *
 * @param {string} gameIdString - The game identifier for game-specific logic
 * @param {Array} figures - Optional figures array from API
 */
const NumberGrid = ({
  onNumberClick,
  placedBets = [],
  maxBetPerOption = 1000,
  onMaxBetReached,
  isDeletionMode = false,
  figures = [], // New prop for API figures
}) => {
  // Get animal data from API figures or use fallback
  const getAnimalData = () => {
    if (figures && figures.length > 0) {
      const transformed = transformFiguresToAnimalData(figures);
      return transformed;
    }

    // Fallback to hardcoded data for 57 animals
    // Special animals: 0-Delfín (green), 00-Ballena (green)
    const animals = [
      {number: 0, name: "delfin", color: "green", displayName: "Delfín"},
      {number: "00", name: "ballena", color: "green", displayName: "Ballena"},
    ];

    // Generate 55 regular animals (1-55) with alternating colors based on image
    for (let i = 1; i <= 55; i++) {
      animals.push({
        number: i,
        name: `animal${i}`,
        color: i % 2 === 1 ? "red" : "dark",
        displayName: `Animal ${i}`,
      });
    }

    return animals;
  };

  const animalData = getAnimalData();

  // Separate special numbers (0, 00) from regular numbers (1-55)
  // Check for various formats: number, string, "0", 0, "00"
  const specialNumbers = animalData.filter(animal => {
    const num = animal.number;
    return num === "00" || num === 0 || num === "0" || String(num) === "00" || String(num) === "0";
  });

  // Sort special numbers in correct order: 0, 00
  specialNumbers.sort((a, b) => {
    const order = {0: 0, "00": 1};
    const aValue = order[a.number];
    const bValue = order[b.number];
    return aValue - bValue;
  });

  // Filter out special numbers - need to check all formats
  const regularNumbers = animalData.filter(animal => {
    const num = animal.number;
    return !(num === "00" || num === 0 || num === "0" || String(num) === "00" || String(num) === "0");
  });

  // Create 5xN grid layout from regular numbers (1-55)
  const createGridLayout = () => {
    const grid = [];
    const rows = Math.ceil(regularNumbers.length / 5); // Calculate number of rows needed

    for (let row = 0; row < rows; row++) {
      const currentRow = [];
      for (let col = 0; col < 5; col++) {
        const index = row * 5 + col;
        if (index < regularNumbers.length) {
          currentRow.push(regularNumbers[index]);
        }
      }
      grid.push(currentRow);
    }
    return grid;
  };

  const gridLayout = createGridLayout();

  // Helper function to get fichas placed on an animal
  const getFichasForAnimal = animalId => {
    const animalBet = placedBets.find(bet => bet.animalId === animalId);
    return animalBet ? animalBet.fichas : [];
  };

  // Helper function to calculate total bet for a specific animal
  const getTotalBetForAnimal = animalId => {
    const fichas = getFichasForAnimal(animalId);
    return fichas.reduce((sum, ficha) => sum + ficha.value, 0);
  };

  // Helper function to render bet chip with sum
  const renderBetChip = animalId => {
    const fichas = getFichasForAnimal(animalId);
    if (fichas.length === 0) return null;

    const totalBet = getTotalBetForAnimal(animalId);

    return (
      <div
        className={`${betChipCls} ${isDeletionMode ? betChipDeletionModeCls : ""}`}
        title={isDeletionMode ? `Click para eliminar ($${totalBet})` : `Total apostado: ${totalBet}`}
      >
        <div className={betChipAmountCls}>{totalBet}</div>
      </div>
    );
  };

  const handleAnimalClick = animal => {
    const animalId = animal.number === "00" || animal.number === "0" ? animal.number : animal.number;

    // If in deletion mode, skip limit check and let parent handle it
    if (isDeletionMode) {
      if (onNumberClick) {
        onNumberClick({
          betType: "animal",
          value: animal.number,
          name: animal.name,
          odds: "55:1",
        });
      }
      return;
    }

    // Get the specific max bet for this animal (from API) or use default
    const animalMaxBet = animal.maxBet || maxBetPerOption;

    // Only check max bet limit when NOT in deletion mode
    const currentTotal = getTotalBetForAnimal(animalId);
    if (currentTotal >= animalMaxBet) {
      // Notify parent component about max bet reached
      if (onMaxBetReached) {
        onMaxBetReached(animalId, animalMaxBet);
      }
      return; // Don't proceed with bet
    }

    if (onNumberClick) {
      onNumberClick({
        betType: "animal",
        value: animal.number,
        name: animal.name,
        odds: "55:1",
        maxBet: animalMaxBet, // Pass the specific max bet to parent
      });
    }
  };

  return (
    <div className={gridContainerCls}>
      {/* Top section with 2 special numbers (0, 00) - Dynamic from API */}
      <div className={topSectionH180C2Cls}>
        {specialNumbers.map((animal, index) => {
          const imageUrl = animal.imageUrl || `/src/assets/images/lottery-icons/${animal.name}.svg`;
          // Apply specific classes based on position
          const specialClass = index === 0 ? special1Cls : special2Cls;

          return (
            <button
              key={animal.number}
              type="button"
              className={`${specialButtonBaseCls} ${specialClass}`}
              onClick={() => handleAnimalClick(animal)}
            >
              <span className={specialNumberCls}>{animal.number}</span>
              <img
                src={imageUrl}
                alt={animal.displayName}
                className={animalIconCls}
                loading="eager"
                onError={e => {
                  e.target.src = `/src/assets/images/lottery-icons/${animal.name}.svg`;
                }}
              />
              {renderBetChip(animal.number)}
            </button>
          );
        })}

        {/* Fallback for hardcoded special numbers if no API data */}
        {specialNumbers.length === 0 && (
          <>
            <button
              type="button"
              className={`${specialButtonBaseCls} ${special1Cls}`}
              onClick={() => handleAnimalClick({number: 0, name: "delfin", maxBet: maxBetPerOption})}
            >
              <span className={specialNumberCls}>0</span>
              <img
                src="/src/assets/images/lottery-icons/delfin.svg"
                alt="Delfín"
                className={animalIconCls}
                loading="eager"
              />
              {renderBetChip(0)}
            </button>

            <button
              type="button"
              className={`${specialButtonBaseCls} ${special2Cls}`}
              onClick={() => handleAnimalClick({number: "00", name: "ballena", maxBet: maxBetPerOption})}
            >
              <span className={specialNumberCls}>00</span>
              <img
                src="/src/assets/images/lottery-icons/ballena.svg"
                alt="Ballena"
                className={animalIconCls}
                loading="eager"
              />
              {renderBetChip("00")}
            </button>
          </>
        )}
      </div>

      {/* Main animal grid - 5 columns, 11 rows for 55 animals */}
      <div className={animalGridCls}>
        {gridLayout.map((row, rowIndex) => (
          <div
            key={`row-${row[0]?.number || `empty-${rowIndex}`}`}
            className={animalRowC5Cls}
          >
            {row.map(animal => (
              <button
                key={animal.number}
                type="button"
                className={`${animalButtonH156Cls} ${colorClsMap[animal.color]} ${animal.name === "animal51" ? bottomLeftCls : animal.name === "animal55" ? bottomRightCls : ""}`}
                onClick={() => handleAnimalClick(animal)}
              >
                <span className={animalNumberCls}>{animal.number.toString().padStart(2, "0")}</span>
                <img
                  src={animal.imageUrl || `/src/assets/images/lottery-icons/${animal.name}.svg`}
                  alt={animal.displayName || animal.name}
                  className={animalIconCls}
                  loading="eager"
                  onError={e => {
                    e.target.src = `/src/assets/images/lottery-icons/${animal.name}.svg`;
                  }}
                />
                {renderBetChip(animal.number)}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

NumberGrid.propTypes = {
  gameIdString: PropTypes.string.isRequired,
  onNumberClick: PropTypes.func,
  placedBets: PropTypes.arrayOf(
    PropTypes.shape({
      animalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      animalName: PropTypes.string,
      fichas: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          value: PropTypes.number,
          name: PropTypes.string,
        })
      ),
    })
  ),
  maxBetPerOption: PropTypes.number,
  onMaxBetReached: PropTypes.func,
  isDeletionMode: PropTypes.bool,
  figures: PropTypes.arrayOf(PropTypes.object),
};

export default NumberGrid;
